import axios from 'axios';
import apiClient from './api';
import { 
  PlanDetails, 
  Address, 
  Subscription,
  DeliverySettings,
  SubscriptionStatus as ApiSubscriptionStatus, 
  PlanType as ApiPlanType,
  ServiceType as ApiServiceType,
  ApiResponse,
  CurrencyCode,
  PlanItem
} from '../types/api';
import { SubscriptionState, Schedule } from '../types';
import { authService } from './index';
import configService from './configService';

// Types for API requests and responses
export type PlanType = 'standard' | 'premium';
export type SubscriptionStatus = 'active' | 'paused' | 'cancelled' | 'pending' | 'pending_renewal' | 'past_due';

// Extended Schedule type with currency
export interface ScheduleWithCurrency extends Schedule {
  currency?: CurrencyCode;
  referral_code?: string;
}

// Payment status response interface
interface PaymentStatusResponse {
  status: string;
  paymentId: string | null;
  message?: string;
}

// Subscription preferences response interface
interface PreferencesResponse {
  preferences: Record<string, boolean>;
}

// In the CreateSubscriptionParams interface, add service_type parameter
export interface CreateSubscriptionParams {
  address_id: string;
  plan_type: string;
  service_type?: ApiServiceType;
  delivery_slots?: string[];
  delivery_day?: string;
  gift_recipient_name?: string;
  gift_recipient_phone?: string;
  gift_message?: string;
  sender_name?: string;
  sender_phone?: string;
  is_gift?: boolean;
  gift_type?: 'direct' | 'code';
  promotion_code?: string;
  referral_code?: string;
  currency?: string;
}

// Add interface definitions for API requests
interface UpdateSubscriptionAddressRequest {
  /** The ID of the address to update the subscription to */
  id: string;
}

// Add interface definitions for API responses where needed
interface UpdateSubscriptionAddressResponse {
  id: string;
  address_id: string;
  message?: string;
}

const subscriptionService = {
  /**
   * Get all available subscription plans
   */
  getPlans: async (): Promise<PlanItem[]> => {
    try {
      // Create a separate instance for public endpoints to avoid auth interceptors
      // This will prevent 401 errors for unauthenticated users
      const publicEndpoint = axios.create({
        baseURL: apiClient.defaults.baseURL,
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      const response = await publicEndpoint.get(`/subscriptions/plans`);
      
      // Store the serviceTypes in localStorage if they are available
      if (response.data.serviceTypes) {
        localStorage.setItem('piika_service_types', JSON.stringify(response.data.serviceTypes));
      }
      
      // API returns { data: plans } structure
      return response.data.data || [];
    } catch (error: any) {
      console.error('Error fetching plans:', error);
      
      // Get fallback prices from configuration service instead of hardcoding
      try {
        const flowerPricing = await configService.getFallbackPricing('flower');
        return [
          {
            id: 'standard-fallback',
            type: 'standard',
            price: flowerPricing.standard.thb || 399,
            features: []
          },
          {
            id: 'premium-fallback',
            type: 'premium',
            price: flowerPricing.premium.thb || 599,
            features: []
          }
        ];
      } catch (configError) {
        console.error('Error fetching fallback config:', configError);
        // Final hardcoded fallback as last resort
        return [
          {
            id: 'standard-fallback',
            type: 'standard',
            price: 399,
            features: []
          },
          {
            id: 'premium-fallback',
            type: 'premium',
            price: 599,
            features: []
          }
        ];
      }
    }
  },
  
  /**
   * Get all user subscriptions
   * @param forceRefresh Force a fresh request bypassing potential caching
   * @returns Promise<Subscription[]> Array of user subscriptions
   */
  getAllSubscriptions: async (forceRefresh: boolean = false): Promise<Subscription[]> => {
    try {
      // Use the same endpoint for all requests since /all is now merged into /subscriptions
      const endpoint = '/subscriptions';
      
      // Add showAllStatuses parameter if force refresh is requested
      const queryParams = forceRefresh ? '?showAllStatuses=true' : '';
      
      const response = await apiClient.get<ApiResponse<Subscription[]>>(`${endpoint}${queryParams}`);
      
      // If data is available, return it
      if (response.data && response.data.data) {
        return response.data.data;
      }
      
      // Empty data case
      return [];
    } catch (error: any) {
      console.error('Error fetching user subscriptions:', error);
      
      // Check if this is a 401 unauthorized error - let the API interceptor handle it
      if (error.response && error.response.status === 401) {
        // This error will be caught by the API interceptor which will redirect to login
        throw error;
      }
      
      throw error;
    }
  },
  
  /**
   * Get the current user's subscription
   * @param forceRefresh Force a fresh request bypassing potential caching
   */
  getCurrentSubscription: async (forceRefresh: boolean = false): Promise<Subscription | null> => {
    try {
      const subscriptions = await subscriptionService.getAllSubscriptions(forceRefresh);
      return subscriptions.length > 0 ? subscriptions[0] : null;
    } catch (error: any) {
      console.error('Error fetching current subscription:', error);
      
      // Check if this is a 401 unauthorized error - let the API interceptor handle it
      if (error.response && error.response.status === 401) {
        // This error will be caught by the API interceptor which will redirect to login
        throw error;
      }
      
      return null;
    }
  },
  
  /**
   * Create a new subscription
   */
  subscribe: async (
    type: string, 
    address: Address, 
    schedule: ScheduleWithCurrency,
    serviceType: ApiServiceType = 'flower',
    subscriptionType: 'monthly' | 'yearly' | 'trial' = 'monthly'
  ): Promise<Subscription> => {
    try {
      // Check if user is authenticated before making the request
      const token = localStorage.getItem('authToken');
      if (!token) {
        // Redirect to login only if not already on subscription page
        const currentPath = window.location.pathname;
        window.location.href = `/login?returnTo=${encodeURIComponent(currentPath)}`;
        throw new Error('Authentication required');
      }

      // Also include deliveryDay for backward compatibility
      const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
      const dayIndex = typeof schedule.dayOfWeek === 'number' ? schedule.dayOfWeek : 1;
      const deliveryDay = dayNames[dayIndex] || "Monday";
      
      const response = await apiClient.post(`/subscriptions`, {
        plan: type,
        service_type: serviceType,
        subscriptionType: subscriptionType,
        addressId: address.id,
        // Send full schedule object
        schedule: {
          dayOfWeek: dayIndex,
          timeSlots: schedule.timeSlots || ['9:00 AM - 12:00 PM']
        },
        deliveryDay, // For backward compatibility
        currency: schedule.currency || 'thb', // Add currency support
        referral_code: schedule.referral_code // Add referral_code support
      });

      return response.data;
    } catch (error: any) {
      console.error('Error creating subscription:', error);
      
      // Check if this is a 401 unauthorized error
      if (error.response && error.response.status === 401) {
        // Check if we're on the subscription page
        const currentPath = window.location.pathname;
        const isSubscriptionPage = currentPath.includes('/subscription');

        // Only redirect if not on subscription page
        if (!isSubscriptionPage) {
          // Redirect to login with return path
          window.location.href = `/login?returnTo=${encodeURIComponent(currentPath)}`;
        }
        throw new Error('Authentication required. Please log in to continue.');
      }
      
      throw error;
    }
  },
  
  /**
   * Update delivery settings for a subscription
   */
  updateDeliverySettings: async (subscriptionId: string, settings: {
    dayOfWeek: number;
    timeSlots: string[];
  }): Promise<any> => {
    try {
      const response = await apiClient.post(`/subscriptions/${subscriptionId}/deliveries/settings`, {
        day_of_week: settings.dayOfWeek,
        time_slots: settings.timeSlots
      });
      return response.data;
    } catch (error: any) {
      console.error('Error updating delivery settings:', error);
      
      // Check if this is a 401 unauthorized error - let the API interceptor handle it
      if (error.response && error.response.status === 401) {
        // This error will be caught by the API interceptor which will redirect to login
        throw error;
      }
      
      throw error;
    }
  },
  
  /**
   * Update subscription status (pause, resume, cancel)
   */
  updateStatus: async (subscriptionId: string, status: SubscriptionStatus): Promise<Subscription> => {
    try {
      const response = await apiClient.put(`/subscriptions/${subscriptionId}/status`, { status });
      return response.data.data; // API returns data wrapped in a data property
    } catch (error: any) {
      console.error('Error updating subscription status:', error);
      
      // Check if this is a 401 unauthorized error - let the API interceptor handle it
      if (error.response && error.response.status === 401) {
        // This error will be caught by the API interceptor which will redirect to login
        throw error;
      }
      
      throw error;
    }
  },
  
  /**
   * Upgrade subscription plan
   */
  upgradePlan: async (subscriptionId: string, newPlan: PlanType): Promise<any> => {
    try {
      const response = await apiClient.post(`/subscriptions/upgrade`, { 
        plan: newPlan,
        subscriptionId: subscriptionId 
      });
      return response.data;
    } catch (error: any) {
      console.error('Error upgrading subscription:', error);
      
      // Check if this is a 401 unauthorized error - let the API interceptor handle it
      if (error.response && error.response.status === 401) {
        // This error will be caught by the API interceptor which will redirect to login
        throw error;
      }
      
      throw error;
    }
  },
  
  /**
   * Create a direct gift subscription
   */
  createDirectGift: async (giftData: {
    plan: PlanType;
    service_type?: ApiServiceType;
    recipient_name: string;
    recipient_phone: string;
    address_id: string;
    message?: string;
    sender_name?: string;
    sender_phone?: string;
    currency?: CurrencyCode;
  }): Promise<Subscription> => {
    try {
      // Check if user is authenticated before making the request
      const token = localStorage.getItem('authToken');
      if (!token) {
        // Redirect to login only if not already on subscription page
        const currentPath = window.location.pathname;
        window.location.href = `/login?returnTo=${encodeURIComponent(currentPath)}`;
        throw new Error('Authentication required');
      }

      const response = await apiClient.post('/subscriptions/gift/direct', giftData);
      return response.data;
    } catch (error: any) {
      console.error('Error creating direct gift subscription:', error);
      
      // Check if this is a 401 unauthorized error
      if (error.response && error.response.status === 401) {
        // Check if we're on the subscription page
        const currentPath = window.location.pathname;
        const isSubscriptionPage = currentPath.includes('/subscription');

        // Only redirect if not on subscription page
        if (!isSubscriptionPage) {
          // Redirect to login with return path
          window.location.href = `/login?returnTo=${encodeURIComponent(currentPath)}`;
        }
        throw new Error('Authentication required. Please log in to continue.');
      }
      
      throw error;
    }
  },

  /**
   * Create a gift code subscription
   */
  createGiftCode: async (giftData: {
    plan: PlanType;
    service_type?: ApiServiceType;
    message?: string;
    expires_at?: string;
    currency?: CurrencyCode;
  }): Promise<{
    payment_id: string;
    amount: number;
    currency: string;
    plan: PlanType;
    service_type: ApiServiceType;
  }> => {
    try {
      const response = await apiClient.post('/subscriptions/gift/code', giftData);
      return response.data;
    } catch (error: any) {
      console.error('Error creating gift code payment:', error);
      
      // Check if this is a 401 unauthorized error - let the API interceptor handle it
      if (error.response && error.response.status === 401) {
        // This error will be caught by the API interceptor which will redirect to login
        throw error;
      }
      
      throw error;
    }
  },

  /**
   * Validate a gift code
   */
  validateGiftCode: async (code: string): Promise<{
    isValid: boolean;
    giftCode: {
      id: string;
      code: string;
      status: string;
      message?: string;
      expiresAt: string;
    };
    subscription: {
      plan: PlanType;
      vases: number;
      price: number;
    };
  }> => {
    try {
      const response = await apiClient.get(`/gift/validate/${code}`);
      return response.data;
    } catch (error: any) {
      console.error('Error validating gift code:', error);
      
      // Check if this is a 401 unauthorized error - let the API interceptor handle it
      if (error.response && error.response.status === 401) {
        // This error will be caught by the API interceptor which will redirect to login
        throw error;
      }
      
      throw error;
    }
  },

  /**
   * Redeem a gift code
   */
  redeemGiftCode: async (code: string, addressId: string): Promise<{
    message: string;
    subscription: Subscription;
  }> => {
    try {
      const response = await apiClient.post(`/gift/redeem/${code}`, {
        address_id: addressId
      });
      return response.data;
    } catch (error: any) {
      console.error('Error redeeming gift code:', error);
      
      // Check if this is a 401 unauthorized error - let the API interceptor handle it
      if (error.response && error.response.status === 401) {
        // This error will be caught by the API interceptor which will redirect to login
        throw error;
      }
      
      throw error;
    }
  },

  /**
   * Claim a direct gift subscription
   */
  claimDirectGift: async (subscriptionId: string, phone: string): Promise<{
    message: string;
    subscription: Subscription;
  }> => {
    try {
      const response = await apiClient.post(`/gift/claim/${subscriptionId}`, {
        phone
      });
      return response.data;
    } catch (error: any) {
      console.error('Error claiming direct gift subscription:', error);
      
      // Check if this is a 401 unauthorized error - let the API interceptor handle it
      if (error.response && error.response.status === 401) {
        // This error will be caught by the API interceptor which will redirect to login
        throw error;
      }
      
      throw error;
    }
  },

  /**
   * Get gifted subscriptions (ones the current user has given)
   */
  getGiftedSubscriptions: async (): Promise<Subscription[]> => {
    try {
      const response = await apiClient.get('/subscriptions/gifted');
      return response.data.data || [];
    } catch (error: any) {
      console.error('Error fetching gifted subscriptions:', error);
      
      // Check if this is a 401 unauthorized error - let the API interceptor handle it
      if (error.response && error.response.status === 401) {
        // This error will be caught by the API interceptor which will redirect to login
        throw error;
      }
      
      return [];
    }
  },

  /**
   * Get received gift subscriptions (ones the current user has received)
   */
  getReceivedGiftSubscriptions: async (): Promise<Subscription[]> => {
    try {
      const response = await apiClient.get('/subscriptions/received');
      return response.data.data || [];
    } catch (error: any) {
      console.error('Error fetching received gift subscriptions:', error);
      
      // Check if this is a 401 unauthorized error - let the API interceptor handle it
      if (error.response && error.response.status === 401) {
        // This error will be caught by the API interceptor which will redirect to login
        throw error;
      }
      
      return [];
    }
  },

  /**
   * Check the payment status for a subscription payment
   */
  checkPaymentStatus: async (clientSecret: string): Promise<PaymentStatusResponse> => {
    try {
      const response = await apiClient.get<ApiResponse<PaymentStatusResponse>>(`/payment/status?client_secret=${clientSecret}`);
      
      if (response.data && response.data.data) {
        return response.data.data;
      }
      
      return { status: 'unknown', paymentId: null };
    } catch (error: any) {
      console.error('Error checking payment status:', error);
      
      // Check if this is a 401 unauthorized error - let the API interceptor handle it
      if (error.response && error.response.status === 401) {
        // This error will be caught by the API interceptor which will redirect to login
        throw error;
      }
      
      return { status: 'error', paymentId: null };
    }
  },

  /**
   * Update the address for a subscription
   * 
   * @param subscriptionId - The ID of the subscription to update
   * @param address - The address object containing the address ID
   * @returns Promise with the updated subscription or null if the update failed
   * 
   * @apiEndpoint PATCH /subscriptions/:id/address
   * @apiRequest { id: string } - The address ID to set for the subscription 
   * @apiResponse { data: { id: string, address_id: string }, message: string }
   */
  updateSubscriptionAddress: async (
    subscriptionId: string,
    address: Address
  ): Promise<SubscriptionState | null> => {
    try {
      // Send the exact parameter name expected by the API endpoint
      const requestBody: UpdateSubscriptionAddressRequest = { 
        id: address.id 
      };

      const response = await apiClient.patch<ApiResponse<UpdateSubscriptionAddressResponse>>(
        `/subscriptions/${subscriptionId}/address`,
        requestBody
      );
      
      if (response.data && response.data.data) {
        return response.data.data as unknown as SubscriptionState;
      }
      
      return null;
    } catch (error: any) {
      console.error('Error updating subscription address:', error);
      
      // Check if this is a 401 unauthorized error - let the API interceptor handle it
      if (error.response && error.response.status === 401) {
        // This error will be caught by the API interceptor which will redirect to login
        throw error;
      }
      
      return null;
    }
  },

  /**
   * Get a single subscription by ID
   */
  getSubscription: async (subscriptionId: string): Promise<Subscription | null> => {
    try {
      const response = await apiClient.get(`/subscriptions/${subscriptionId}`);
      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching subscription:', error);
      
      // Check if this is a 401 unauthorized error - let the API interceptor handle it
      if (error.response && error.response.status === 401) {
        // This error will be caught by the API interceptor which will redirect to login
        throw error;
      }
      
      return null;
    }
  },

  /**
   * Get preferences for a specific subscription
   * @param subscriptionId ID of the subscription
   * @returns Promise<PreferencesResponse> Object containing preferences
   */
  getSubscriptionPreferences: async (subscriptionId: string): Promise<PreferencesResponse> => {
    try {
      const response = await apiClient.get<ApiResponse<PreferencesResponse>>(
        `/subscriptions/${subscriptionId}/preferences`
      );
      
      if (response.data && response.data.data) {
        return response.data.data;
      }
      
      // Return empty preferences if none found
      return { preferences: {} };
    } catch (error: any) {
      console.error('Error fetching subscription preferences:', error);
      
      // Check if this is a 401 unauthorized error - let the API interceptor handle it
      if (error.response && error.response.status === 401) {
        // This error will be caught by the API interceptor which will redirect to login
        throw error;
      }
      
      // Return empty preferences on other errors
      return { preferences: {} };
    }
  },
  
  /**
   * Update preferences for a specific subscription
   * @param subscriptionId ID of the subscription
   * @param preferences Object containing preferences
   * @returns Promise<PreferencesResponse> Updated preferences object
   */
  updateSubscriptionPreferences: async (
    subscriptionId: string, 
    preferences: Record<string, boolean>
  ): Promise<PreferencesResponse> => {
    try {
      const response = await apiClient.put<ApiResponse<PreferencesResponse>>(
        `/subscriptions/${subscriptionId}/preferences`,
        { preferences }
      );
      
      if (response.data && response.data.data) {
        return response.data.data;
      }
      
      // Return the provided preferences if response doesn't include data
      return { preferences };
    } catch (error: any) {
      console.error('Error updating subscription preferences:', error);
      
      // Check if this is a 401 unauthorized error - let the API interceptor handle it
      if (error.response && error.response.status === 401) {
        // This error will be caught by the API interceptor which will redirect to login
        throw error;
      }
      
      throw error;
    }
  },
};

export default subscriptionService; 